// Everything between the player's hands and a chosen move: pointer events,
// the keyboard cursor, the gear icon, and the promotion picker. Owns the
// MoveSelector; the scene feeds it positions and receives finished moves.
import { gearHitTest } from '@bn-games/render-helpers';
import { CanvasTooltip, announce } from '@bn-games/ui-shell';
import { InputHandler, type PointerInteraction } from '../input/InputHandler.js';
import { MoveSelector, choosePromotion, type SelectorResult, type SelectorView } from '../input/MoveSelector.js';
import { KeyboardPlay } from '../a11y/KeyboardPlay.js';
import { narrateCursor, narrateSelection, type Translate } from '../a11y/narration.js';
import { askPromotion, type PromotionPrompt } from '../ui/promotionModal.js';
import { EMPTY, type Color, type Move, type Square } from '../game/types.js';
import type { ViewerRotation } from '../render/geometry.js';
import type { FrameIcons } from './SceneRender.js';
import { t } from '../i18n/I18n.js';

export interface BoardInteractionDeps {
  readonly canvas: HTMLCanvasElement;
  readonly getRotation: () => ViewerRotation;
  /** Where the corner icons (gear, move-list toggle) were last drawn. */
  readonly getIcons: () => FrameIcons | null;
  readonly onGear: () => void;
  /** The move list's toggle icon: whether the list is showing, and the flip. */
  readonly moveList: { readonly shown: () => boolean; readonly toggle: () => void };
  readonly onMove: (move: Move) => void;
  /** Any input activity (AFK timer poke). */
  readonly onActivity: () => void;
  readonly translate: Translate;
}

export class BoardInteraction {
  private readonly selector = new MoveSelector();
  private readonly input: InputHandler;
  private readonly keyboard: KeyboardPlay;
  private board: Int8Array = new Int8Array(64);
  private hovered: Square | null = null;
  private gearHovered = false;
  private moveListHovered = false;
  private promotion: PromotionPrompt | null = null;
  private readonly tooltip: CanvasTooltip;
  /** A press that began on a corner icon: it acts on release, like every other game's gear. */
  private pressedIcon: 'gear' | 'moveList' | null = null;
  private disposed = false;

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (this.promotion !== null) return;
    this.deps.onActivity();
    if (this.keyboard.handleKey(e.key)) e.preventDefault();
  };
  // `:focus-visible` is the browser's own answer to "did the keyboard put the focus here?"
  private readonly onFocus = (): void => this.keyboard.setFocused(true, this.deps.canvas.matches(':focus-visible'));
  private readonly onBlur = (): void => this.keyboard.setFocused(false);

  constructor(private readonly deps: BoardInteractionDeps) {
    this.input = new InputHandler({
      canvas: deps.canvas,
      getRotation: deps.getRotation,
      onInteraction: (e) => this.onPointer(e),
    });
    this.keyboard = new KeyboardPlay({
      getRotation: deps.getRotation,
      activate: (sq) => this.handle(this.selector.activate(sq), true),
      onCursorMove: (sq) => announce(this.cursorText(sq)),
    });
    this.tooltip = new CanvasTooltip(deps.canvas);
    deps.canvas.addEventListener('keydown', this.onKeyDown);
    deps.canvas.addEventListener('focus', this.onFocus);
    deps.canvas.addEventListener('blur', this.onBlur);
  }

  /** New position (or not our turn: pass no moves). Clears any selection. */
  setPosition(board: Int8Array, turn: Color, moves: readonly Move[]): void {
    this.board = board;
    this.selector.setPosition(board, turn, moves);
  }

  view(): SelectorView & { readonly hovered: Square | null; readonly gearHovered: boolean; readonly moveListHovered: boolean; readonly focus: Square | null } {
    const { hovered, gearHovered, moveListHovered } = this;
    return { ...this.selector.view(), hovered, gearHovered, moveListHovered, focus: this.keyboard.focusSquare() };
  }

  dispose(): void {
    this.disposed = true;
    this.promotion?.close();
    this.promotion = null;
    this.input.dispose();
    this.tooltip.dispose();
    this.deps.canvas.removeEventListener('keydown', this.onKeyDown);
    this.deps.canvas.removeEventListener('focus', this.onFocus);
    this.deps.canvas.removeEventListener('blur', this.onBlur);
  }

  private onPointer(e: PointerInteraction): void {
    const at = { x: e.canvasX, y: e.canvasY };
    const icons = this.deps.getIcons();
    this.gearHovered = icons !== null && gearHitTest(at, icons.gear);
    this.moveListHovered = icons !== null && gearHitTest(at, icons.moveList);
    if (e.kind === 'leave') this.gearHovered = this.moveListHovered = false;
    this.syncTooltip();
    if (e.kind === 'down') this.keyboard.pointerUsed();
    if (this.iconPress(e.kind)) return;
    if (this.promotion !== null) return;
    if (e.kind !== 'move') this.deps.onActivity();
    switch (e.kind) {
      case 'down':
        this.handle(this.selector.pointerDown(e.square, at), false);
        break;
      case 'move':
        this.hovered = e.square;
        this.selector.pointerMove(at);
        break;
      case 'up':
        this.handle(this.selector.pointerUp(e.square), false);
        break;
      case 'leave':
        this.hovered = null;
        break;
      case 'cancel':
        this.selector.cancelGesture();
        break;
    }
  }

  /** Name the corner icon under the pointer, or hide the label. */
  private syncTooltip(): void {
    if (this.gearHovered) this.tooltip.set(t('game.tooltips.pause'));
    else if (this.moveListHovered) {
      this.tooltip.set(t(this.deps.moveList.shown() ? 'game.tooltips.hideMoveList' : 'game.tooltips.showMoveList'));
    } else this.tooltip.set(null);
  }

  /**
   * Presses on the corner icons. One fires when the pointer is released over
   * the icon it went down on. Returns true when the event belonged to an
   * icon, so the board never sees it.
   */
  private iconPress(kind: PointerInteraction['kind']): boolean {
    const over = this.gearHovered ? 'gear' : this.moveListHovered ? 'moveList' : null;
    if (kind === 'down') {
      this.pressedIcon = over;
      return over !== null;
    }
    if (kind !== 'up' && kind !== 'cancel') return false;
    const pressed = this.pressedIcon;
    this.pressedIcon = null;
    if (pressed === null) return false;
    if (kind === 'up' && pressed === over) {
      if (pressed === 'moveList') this.deps.moveList.toggle();
      else {
        // The menu covers the icon: its label would be left hanging over it.
        this.gearHovered = false;
        this.deps.onGear();
      }
      this.syncTooltip();
    }
    return true;
  }

  private handle(result: SelectorResult, fromKeyboard: boolean): void {
    if (result.kind === 'move') {
      this.deps.onMove(result.move);
      return;
    }
    if (result.kind === 'promote') {
      const prompt = askPromotion();
      this.promotion = prompt;
      void prompt.choice.then((type) => {
        // Closed by dispose(): the game this choice belonged to is gone.
        if (this.disposed) return;
        this.promotion = null;
        const move = type === null ? undefined : choosePromotion(result.options, type);
        if (move !== undefined) this.deps.onMove(move);
        this.deps.canvas.focus();
      });
      return;
    }
    if (fromKeyboard) {
      const selected = this.selector.getSelected();
      announce(narrateSelection(this.deps.translate, selected, selected === null ? EMPTY : this.board[selected] ?? EMPTY));
    }
  }

  private cursorText(sq: Square): string {
    const target = this.selector.view().legalTargets.find((x) => x.square === sq);
    const kind = target === undefined ? 'none' : target.capture ? 'capture' : 'move';
    return narrateCursor(this.deps.translate, sq, this.board[sq] ?? EMPTY, kind);
  }
}
